// ============================================================
// ADVANCE YEARLY OCCURRENCE
// ============================================================
//
// Returns the NEXT scheduled yearly occurrence after currentDate.
//
// Yearly rules:
//
// - month_of_year determines the month.
// - day_of_month determines the day.
// - If month/day are not configured, recurrence_start_date
//   supplies the month/day.
// - interval_value determines how many years between
//   recurrence years.
// - Recurrence years are anchored to recurrence_start_date.
//
// IMPORTANT:
// If the requested day does not exist in a particular year,
// the occurrence is placed on the LAST VALID DAY of that month.
//
// Example:
//
// February 29, every year:
//
//   2024 → February 29
//   2025 → February 28
//   2026 → February 28
//   2027 → February 28
//   2028 → February 29
//
// This preserves the user's intended yearly calendar date
// instead of skipping the occurrence in non-leap years.
//
// ============================================================


// ============================================================
// LOCAL CALENDAR DATE
// ============================================================
//
// Database DATE values represent calendar dates.
// Preserve the calendar date rather than allowing timezone
// conversion to change the day.
// ============================================================

function toLocalCalendarDate(value) {

    if (!value) {
        return null
    }

    // --------------------------------------------------------
    // JavaScript Date
    // --------------------------------------------------------

    if (value instanceof Date) {

        return new Date(
            value.getFullYear(),
            value.getMonth(),
            value.getDate()
        )
    }

    // --------------------------------------------------------
    // Raw YYYY-MM-DD
    // --------------------------------------------------------

    if (
        typeof value === 'string' &&
        /^\d{4}-\d{2}-\d{2}$/.test(value)
    ) {

        const [
            year,
            month,
            day
        ] =
            value
                .split('-')
                .map(Number)

        return new Date(
            year,
            month - 1,
            day
        )
    }

    // --------------------------------------------------------
    // Fallback
    // --------------------------------------------------------

    const parsed =
        new Date(value)

    if (
        Number.isNaN(
            parsed.getTime()
        )
    ) {
        return null
    }

    return new Date(
        parsed.getFullYear(),
        parsed.getMonth(),
        parsed.getDate()
    )
}


// ============================================================
// GET DAYS IN MONTH
// ============================================================

function getDaysInMonth(
    year,
    month
) {

    return new Date(
        year,
        month,
        0
    ).getDate()
}


// ============================================================
// ADVANCE YEARLY OCCURRENCE
// ============================================================

export function advanceYearly({

    template,

    currentDate

}) {

    // ========================================================
    // CURRENT DATE
    // ========================================================

    const current =
        toLocalCalendarDate(
            currentDate
        )

    if (!current) {
        return null
    }


    // ========================================================
    // RECURRENCE START DATE
    // ========================================================

    const startDate =
        toLocalCalendarDate(
            template.recurrence_start_date
        )


    // ========================================================
    // DETERMINE MONTH
    // ========================================================
    //
    // Match the yearly evaluator:
    //
    // 1. Explicit month_of_year
    // 2. Otherwise recurrence_start_date month
    // ========================================================

    const configuredMonth =
        Number(
            template.month_of_year
        )

    const month =
        Number.isInteger(
            configuredMonth
        ) &&
        configuredMonth >= 1 &&
        configuredMonth <= 12

            ? configuredMonth

            : startDate
                ? startDate.getMonth() + 1
                : current.getMonth() + 1


    // ========================================================
    // DETERMINE DAY
    // ========================================================
    //
    // Match the yearly evaluator:
    //
    // 1. Explicit day_of_month
    // 2. Otherwise recurrence_start_date day
    // ========================================================

    const configuredDay =
        Number(
            template.day_of_month
        )

    const requestedDay =
        Number.isInteger(
            configuredDay
        ) &&
        configuredDay >= 1 &&
        configuredDay <= 31

            ? configuredDay

            : startDate
                ? startDate.getDate()
                : current.getDate()


    // ========================================================
    // YEARLY INTERVAL
    // ========================================================
    //
    // NULL / invalid values default to every year.
    // ========================================================

    const interval =
        Math.max(
            1,
            Number(
                template.interval_value
            ) || 1
        )


    // ========================================================
    // RECURRENCE YEAR ANCHOR
    // ========================================================

    const anchorYear =
        startDate
            ? startDate.getFullYear()
            : current.getFullYear()


    // ========================================================
    // SEARCH FOR NEXT RECURRENCE
    // ========================================================
    //
    // Begin with the next calendar year.
    //
    // The year must satisfy the configured interval.
    //
    // ========================================================

    let candidateYear =
        current.getFullYear() + 1


    // --------------------------------------------------------
    // Safety limit.
    //
    // 400 years contains a complete Gregorian leap-year cycle,
    // so this is more than enough to find a valid yearly date.
    // --------------------------------------------------------

    const MAX_YEARS = 400


    for (
        let i = 0;
        i < MAX_YEARS;
        i++
    ) {

        const yearsSinceStart =
            candidateYear -
            anchorYear


        // ----------------------------------------------------
        // Correct recurrence year?
        // ----------------------------------------------------

        if (
            yearsSinceStart >= 0 &&
            yearsSinceStart % interval === 0
        ) {

            // ------------------------------------------------
            // Determine the last valid day of the configured
            // month in this particular year.
            //
            // This is what makes February 29 work correctly
            // in non-leap years.
            // ------------------------------------------------

            const daysInMonth =
                getDaysInMonth(
                    candidateYear,
                    month
                )


            // ------------------------------------------------
            // Clamp the requested day to the final valid day
            // of the month.
            //
            // Examples:
            //
            // Feb 29 → Feb 28 in non-leap years
            // Apr 31 → Apr 30
            // Jun 31 → Jun 30
            // ------------------------------------------------

            const actualDay =
                Math.min(
                    requestedDay,
                    daysInMonth
                )


            return new Date(
                candidateYear,
                month - 1,
                actualDay
            )
        }


        // ----------------------------------------------------
        // Move to the next calendar year.
        // ----------------------------------------------------

        candidateYear++
    }


    // ========================================================
    // No valid future occurrence found.
    // ========================================================

    return null
}